import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ResolvedPos } from '@atlaskit/editor-prosemirror/model';
import {
	type EditorState,
	PluginKey,
	type ReadonlyTransaction,
} from '@atlaskit/editor-prosemirror/state';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';

import type { HubExternalInsertPlugin } from '../hubExternalInsertPluginType';
import {
	createCursorIndicatorDecoration,
	findCursorIndicatorDecs,
} from './create-cursor-indicator-decoration';

const HUB_EXTENSION_DRAG_TYPE = 'hub-extension';

export const key: PluginKey<PluginState> = new PluginKey<PluginState>('hubExternalInsert');

export interface PluginState {
	decorations: DecorationSet;
	/** Document position resolved from the latest mouse coordinates during drag. */
	dropTargetPos: number | null;
	showCursorDropIndicator: boolean;
}

const initialState: PluginState = {
	decorations: DecorationSet.empty,
	showCursorDropIndicator: false,
	dropTargetPos: null,
};

/**
 * Container node types we deliberately descend INTO when resolving a drop
 * position. When the cursor is over content nested inside one of these
 * containers, we keep the resolved position inside the container instead
 * of clamping it back up to the document root.
 *
 * This is what enables drag-and-drop into:
 *   - layouts (`layoutSection` → `layoutColumn`)
 *   - hub `section` extension (a `multiBodiedExtension` with
 *     `extensionFrame` content holes)
 *   - bodied / nested expand / standard bodiedExtension containers
 *
 * Anything not in this set behaves like a leaf for the purposes of drop
 * resolution — the position is clamped to the nearest ancestor in the set
 * (or to the doc root). This matches the deny-list intuition used by
 * `editor-plugin-block-controls` for in-editor drag-and-drop.
 */
const DESCEND_INTO_NODES = new Set<string>([
	'doc',
	'layoutSection',
	'layoutColumn',
	'multiBodiedExtension',
	'extensionFrame',
	'bodiedExtension',
	'expand',
	'nestedExpand',
]);

/**
 * Pick the boundary (before or after the child at the given depth) that
 * matches the cursor's vertical position. Reads the block DOM rect
 * directly to avoid calling `coordsAtPos`, which forces a full
 * synchronous layout reflow and is extremely slow when complex React
 * node views are present.
 */
function pickBoundaryForDepth(
	editorView: EditorView,
	resolvedPos: ResolvedPos,
	depth: number,
	clientY: number,
	rawPos: number,
): number {
	const blockStart = resolvedPos.before(depth);
	const blockEnd = resolvedPos.after(depth);

	const domNode = editorView.nodeDOM(blockStart);
	if (domNode instanceof HTMLElement) {
		const rect = domNode.getBoundingClientRect();
		const blockMidY = (rect.top + rect.bottom) / 2;
		return clientY < blockMidY ? blockStart : blockEnd;
	}

	// Fallback when the DOM node isn't available (e.g. during transient
	// node-view mount): use the proportional position within the block.
	const blockSize = blockEnd - blockStart;
	const offsetInBlock = rawPos - blockStart;
	return offsetInBlock < blockSize / 2 ? blockStart : blockEnd;
}

/**
 * Resolves a document position from mouse coordinates by using
 * ProseMirror's posAtCoords API and walking inward from the cursor's
 * deepest level outward, picking the **innermost** ancestor that we
 * recognise as a "descend-into" container. This means a drop inside a
 * `layoutColumn` resolves to a position inside the column (not above
 * the whole layout), and a drop inside a hub `section` resolves to a
 * position inside the section's content body.
 *
 * Returns the position at the chosen child boundary (above or below
 * the inner block), or null if the coordinates don't map to a position
 * inside the editor.
 *
 * NOTE: We avoid `coordsAtPos` for the same perf reason as before —
 * `pickBoundaryForDepth()` reads `nodeDOM().getBoundingClientRect()`
 * which is cheap.
 */
function resolveDropPosition(
	editorView: EditorView,
	clientX: number,
	clientY: number,
): number | null {
	try {
		const posInfo = editorView.posAtCoords({ left: clientX, top: clientY });
		if (!posInfo) {
			return null;
		}
		const rawPos = Math.min(posInfo.pos, editorView.state.doc.content.size);
		const resolvedPos = editorView.state.doc.resolve(rawPos);

		// Walk from the deepest depth INWARD (towards depth 1), stopping
		// at the first ancestor whose parent is a known descend-into
		// container. This is the inverse of the previous `depth >= 1`
		// clamp, which always forced the position up to the document
		// root and prevented drops into layouts / sections / extensions.
		//
		// `resolvedPos.depth` is the parent depth at the cursor; the
		// child sitting at that level lives at `depth + 1`. We walk
		// from the cursor's parent upward, and as soon as we find a
		// parent that is in DESCEND_INTO_NODES we pick the boundary of
		// the child at that level.
		for (let d = resolvedPos.depth; d >= 1; d--) {
			const parent = resolvedPos.node(d - 1);
			if (DESCEND_INTO_NODES.has(parent.type.name)) {
				return pickBoundaryForDepth(editorView, resolvedPos, d, clientY, rawPos);
			}
		}

		// Cursor is at depth 0 (over the doc itself, e.g. between
		// blocks at the very top). Fall back to the raw position.
		return resolvedPos.pos;
	} catch {
		return null;
	}
}

export function createPlugin(
	api: ExtractInjectionAPI<HubExternalInsertPlugin> | undefined,
	nodeViewPortalProviderAPI: PortalProviderAPI | undefined,
): SafePlugin<PluginState> {
	// If portal API is missing (e.g. wrong editor wiring), run as no-op so the page doesn't break
	if (!nodeViewPortalProviderAPI || typeof nodeViewPortalProviderAPI.render !== 'function') {
		return new SafePlugin({
			key,
			state: {
				init: () => initialState,
				apply: (_tr, state) => state,
			},
			props: {
				decorations: (state) => key.getState(state)?.decorations,
			},
		});
	}

	return new SafePlugin({
		key,
		state: {
			init: () => initialState,
			apply: (
				tr: ReadonlyTransaction,
				currentState: PluginState,
				_oldState: EditorState,
				newState: EditorState,
			) => {
				try {
					const meta = tr.getMeta(key) as
						| { dropTargetPos?: number | null; showCursorDropIndicator?: boolean }
						| undefined;

					// If there is no meta for this plugin and the doc hasn't changed,
					// nothing can affect our state — return early to avoid expensive
					// decoration teardown/recreation (which involves React portals).
					if (!meta && !tr.docChanged) {
						return currentState;
					}

					const showCursorDropIndicator =
						meta?.showCursorDropIndicator ?? currentState.showCursorDropIndicator;
					const dropTargetPos =
						meta?.dropTargetPos !== undefined ? meta.dropTargetPos : currentState.dropTargetPos;

					// If nothing meaningful changed, keep the current state as-is
					// to avoid destroying and recreating the React portal decoration.
					if (
						showCursorDropIndicator === currentState.showCursorDropIndicator &&
						dropTargetPos === currentState.dropTargetPos &&
						!tr.docChanged
					) {
						return currentState;
					}

					let decorations = currentState.decorations;

					if (tr.docChanged) {
						decorations = decorations.map(tr.mapping, tr.doc);
					}

					// Only remove and recreate decorations when the indicator
					// visibility or position actually changed.
					const indicatorChanged =
						showCursorDropIndicator !== currentState.showCursorDropIndicator ||
						dropTargetPos !== currentState.dropTargetPos;

					if (indicatorChanged) {
						const cursorDecs = findCursorIndicatorDecs(decorations);
						if (cursorDecs.length > 0) {
							decorations = decorations.remove(cursorDecs);
						}

						if (showCursorDropIndicator && dropTargetPos != null) {
							const clampedPos = Math.min(dropTargetPos, newState.doc.content.size);
							const resolvedPos = newState.doc.resolve(clampedPos);

							// Render the indicator at exactly the position the
							// pointer resolved to. `resolveDropPosition()` has
							// already placed `dropTargetPos` at a valid child
							// boundary inside the innermost descend-into
							// container — the document root, a `layoutColumn`,
							// a hub `section`, etc. We must therefore read
							// the surrounding `prevNode` / `nextNode` /
							// `parentNode` from THAT depth, not from the
							// document root, otherwise the gap/offset hints
							// passed to `createCursorIndicatorDecoration` will
							// describe the wrong siblings and the indicator
							// will visually misalign inside nested containers.
							const indicatorDepth = resolvedPos.depth;
							const parentNode = resolvedPos.node(indicatorDepth);
							const indexInParent = resolvedPos.index(indicatorDepth);
							const prevNode = indexInParent > 0 ? parentNode.child(indexInParent - 1) : null;
							const nextNode =
								indexInParent < parentNode.childCount ? parentNode.child(indexInParent) : null;
							// `lineLength` is intentionally NOT passed any more.
							// The indicator now sizes itself to its container's
							// natural width via CSS (`left: 0; right: 0` on an
							// absolute child of a `width: 100%` host). This is
							// what makes the indicator behave correctly inside
							// `layoutColumn`s of differing widths and inside
							// hub `section` extension frames.
							const dec = createCursorIndicatorDecoration(
								clampedPos,
								nodeViewPortalProviderAPI,
								prevNode,
								nextNode,
								parentNode,
							);
							decorations = decorations.add(newState.doc, [dec]);
						}
					}

					return {
						decorations,
						showCursorDropIndicator,
						dropTargetPos,
					};
				} catch {
					return currentState;
				}
			},
		},
		props: {
			decorations: (state) => key.getState(state)?.decorations,
		},
		view(editorView: EditorView) {
			let cleanup: (() => void) | undefined;
			try {
				// Track pending rAF to throttle onDrag updates to at most once per frame.
				let rafId: number | null = null;

				cleanup = monitorForElements({
					canMonitor: ({ source }) => source.data.type === HUB_EXTENSION_DRAG_TYPE,
					onDragStart: ({ location }) => {
						const coords = location.current.input;
						const dropPos = resolveDropPosition(editorView, coords.clientX, coords.clientY);
						editorView.dispatch(
							editorView.state.tr.setMeta(key, {
								showCursorDropIndicator: true,
								dropTargetPos: dropPos,
							}),
						);
					},
					onDrag: ({ location }) => {
						const coords = location.current.input;
						const clientX = coords.clientX;
						const clientY = coords.clientY;

						// Throttle to one update per animation frame to avoid excessive
						// ProseMirror transactions while the user is dragging.
						if (rafId != null) {
							cancelAnimationFrame(rafId);
						}
						rafId = requestAnimationFrame(() => {
							rafId = null;
							const dropPos = resolveDropPosition(editorView, clientX, clientY);
							const currentState = key.getState(editorView.state);
							// Only dispatch if position actually changed to avoid unnecessary updates
							if (currentState?.dropTargetPos !== dropPos) {
								editorView.dispatch(editorView.state.tr.setMeta(key, { dropTargetPos: dropPos }));
							}
						});
					},
					onDrop: () => {
						if (rafId != null) {
							cancelAnimationFrame(rafId);
							rafId = null;
						}
						// Always ensure the indicator is cleaned up when a drag
						// ends. The EditorDropTarget's onDrop already clears the
						// indicator and inserts content in its own transaction,
						// but we still need this for drags that end outside the
						// drop target (cancelled or missed). The apply function's
						// early-return optimisation ensures this is a no-op when
						// the indicator was already cleared by EditorDropTarget.
						const currentState = key.getState(editorView.state);
						if (currentState?.showCursorDropIndicator) {
							editorView.dispatch(
								editorView.state.tr.setMeta(key, {
									showCursorDropIndicator: false,
									dropTargetPos: null,
								}),
							);
						}
					},
				});
			} catch {}
			return {
				destroy() {
					try {
						cleanup?.();
					} catch {}
				},
			};
		},
	});
}
